import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import { PermissionManager } from '../utils/permissions';
import AuthErrorBanner from './AuthErrorBanner';
import { isAdminMode } from '../utils/domainDetection';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isAdmin = PermissionManager.isArkxAdmin(user) && isAdminMode();
  const homeLink = isAdmin ? '/admin' : '/dashboard';

  // Get role-aware navigation items
  const navigation = PermissionManager.getNavigationItems(user);

  const handleLogout = () => {
    logout();
    navigate(isAdmin ? '/admin/login' : '/login');
  };

  const toggleDropdown = (itemName: string) => {
    setOpenDropdowns(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const isDropdownOpen = (itemName: string) => openDropdowns.includes(itemName);

  const isPathActive = (href?: string, children?: any[]) => {
    if (href) {
      return location.pathname === href || location.pathname.startsWith(href + '/');
    }
    if (children) {
      // Check exact match first, then check if path starts with child href
      // Use stricter matching to avoid /products/launch matching /products
      return children.some(child => {
        if (location.pathname === child.href) return true;
        // Only match as prefix if there's a / after the child href
        if (location.pathname.startsWith(child.href + '/')) return true;
        return false;
      });
    }
    return false;
  };

  // Auto-open dropdowns when sidebar expands if a child is active
  const handleSidebarExpand = () => {
    setSidebarExpanded(true);
    // Auto-open dropdown if a child route is active
    navigation.forEach((item: any) => {
      if (item.children && isPathActive(undefined, item.children)) {
        if (!openDropdowns.includes(item.name)) {
          setOpenDropdowns(prev => [...prev, item.name]);
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-arkx-gray">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-arkx-black/50 backdrop-blur-sm" />
        </div>
      )}

      {/* Mobile sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-out lg:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-5 border-b border-gray-100">
          <Link to={homeLink} className="flex items-center">
            <img
              src="/arkx_black.png"
              alt="ARKX"
              className="h-10 w-auto"
            />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg text-gray-400 hover:text-arkx-black hover:bg-gray-50 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {navigation.map((item: any) => {
            const IconComponent = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isOpen = isDropdownOpen(item.name);
            const isActive = isPathActive(item.href, item.children);

            if (hasChildren) {
              return (
                <div key={item.name} className="mb-1">
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className={`
                      flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                      ${isActive
                        ? 'bg-arkx-black text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-arkx-black'
                      }
                    `}
                  >
                    <IconComponent className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.name}</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 mt-1' : 'max-h-0'}`}>
                    <div className="ml-5 pl-3 border-l border-gray-200 space-y-1">
                      {item.children.map((child: any) => {
                        const ChildIcon = child.icon;
                        const childActive = location.pathname === child.href;
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200
                              ${childActive
                                ? 'bg-gray-100 text-arkx-black font-medium'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-arkx-black'
                              }
                            `}
                          >
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href!}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all duration-200
                  ${isActive
                    ? 'bg-arkx-black text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-arkx-black'
                  }
                `}
              >
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Mobile User menu */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-arkx-black flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.first_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-arkx-black truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.company?.name || 'ARKX Admin'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50 hover:text-arkx-black transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </div>

      {/* Desktop sidebar - Collapsible */}
      <div
        className={`
          hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:flex-col
          bg-white border-r border-gray-100 transition-all duration-300 ease-out
          ${sidebarExpanded ? 'lg:w-64' : 'lg:w-[72px]'}
        `}
        onMouseEnter={handleSidebarExpand}
        onMouseLeave={() => setSidebarExpanded(false)}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-gray-100">
          <Link to={homeLink} className="flex items-center">
            <img
              src="/arkx_black.png"
              alt="ARKX"
              className="h-8 w-auto flex-shrink-0 object-contain"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto overflow-x-hidden">
          {navigation.map((item: any) => {
            const IconComponent = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isOpen = isDropdownOpen(item.name) && sidebarExpanded;
            const isActive = isPathActive(item.href, item.children);

            if (hasChildren) {
              return (
                <div key={item.name} className="mb-1">
                  <button
                    onClick={() => sidebarExpanded && toggleDropdown(item.name)}
                    className={`
                      flex items-center w-full rounded-lg transition-all duration-200 group relative
                      ${sidebarExpanded ? 'px-3 py-2.5 gap-3' : 'px-3 py-2.5 justify-center'}
                      ${isActive
                        ? 'bg-arkx-black text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-arkx-black'
                      }
                    `}
                  >
                    <IconComponent className="w-5 h-5 flex-shrink-0" />
                    {sidebarExpanded && (
                      <>
                        <span className="flex-1 text-left text-sm font-medium whitespace-nowrap">{item.name}</span>
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </>
                    )}
                    {/* Tooltip when collapsed */}
                    {!sidebarExpanded && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-arkx-black text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                        {item.name}
                      </div>
                    )}
                  </button>
                  {sidebarExpanded && (
                    <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 mt-1' : 'max-h-0'}`}>
                      <div className="ml-5 pl-3 border-l border-gray-200 space-y-1">
                        {item.children.map((child: any) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname === child.href;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`
                                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200
                                ${childActive
                                  ? 'bg-gray-100 text-arkx-black font-medium'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-arkx-black'
                                }
                              `}
                            >
                              <ChildIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="whitespace-nowrap">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href!}
                className={`
                  flex items-center rounded-lg mb-1 transition-all duration-200 group relative
                  ${sidebarExpanded ? 'px-3 py-2.5 gap-3' : 'px-3 py-2.5 justify-center'}
                  ${isActive
                    ? 'bg-arkx-black text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-arkx-black'
                  }
                `}
              >
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                {sidebarExpanded && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.name}</span>
                )}
                {/* Tooltip when collapsed */}
                {!sidebarExpanded && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-arkx-black text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                    {item.name}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User menu */}
        <div className="p-3 border-t border-gray-100">
          {sidebarExpanded ? (
            <>
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-arkx-black flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-medium">
                    {user?.first_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-arkx-black truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.company?.name || 'ARKX Admin'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50 hover:text-arkx-black transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                <span>Sign out</span>
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-full p-2.5 text-gray-600 rounded-lg hover:bg-gray-50 hover:text-arkx-black transition-colors group relative"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <div className="absolute left-full ml-2 px-2 py-1 bg-arkx-black text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                Sign out
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-[72px]'}`}>
        {/* Mobile header */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between h-14 px-4 bg-white border-b border-gray-100">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 rounded-lg text-gray-600 hover:text-arkx-black hover:bg-gray-50 transition-colors"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
            <Link to={homeLink} className="flex items-center gap-2">
              <img
                src="/arkx_black.png"
                alt="ARKX"
                className="h-6 w-auto"
              />
            </Link>
            <div className="w-9 h-9 rounded-full bg-arkx-black flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.first_name?.charAt(0) || 'U'}
              </span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <AuthErrorBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
